import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import Footer from "../components/Home/Footer/Footer";
import ImageWithPlaceholder from "../components/common/ImageWithPlaceholder";
import { useCart } from "../components/Cart/cartContext";
import { API_URL } from "../config";
import { demoProducts } from "../data/demoProducts";
import styles from "./productDetail.module.css";

// Store WhatsApp line for direct orders (0810 321 8560 in international format)
const WHATSAPP_NUMBER = "2348103218560";

// Designers list with legacy single-credit fallback
export const getDesigners = (product) => {
  if (product.designers?.length) return product.designers;
  if (product.designerName)
    return [{ name: product.designerName, link: product.designerLink || "" }];
  return [];
};

// Renders "A ↗, B ↗ & C ↗" with links where available
export const DesignerNames = ({ product }) => {
  const designers = getDesigners(product);
  return (
    <>
      {designers.map((designer, i) => (
        <React.Fragment key={`${designer.name}-${i}`}>
          {designer.link ? (
            <a
              href={designerHref(designer.link)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {designer.name} ↗
            </a>
          ) : (
            <strong>{designer.name}</strong>
          )}
          {i < designers.length - 2 ? ", " : i === designers.length - 2 ? " & " : ""}
        </React.Fragment>
      ))}
    </>
  );
};

// Links pasted as "instagram.com/x" or "@x" still need to open correctly
export const designerHref = (link) => {
  const raw = String(link || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("@")) return `https://instagram.com/${raw.slice(1)}`;
  return `https://${raw}`;
};

const SIZES = [
  { label: "XS", details: 'Extra Small — Chest 32"–34", Waist 28–30' },
  { label: "S", details: 'Small — Chest 34"–36", Waist 30–32' },
  { label: "M", details: 'Medium — Chest 38"–40", Waist 32–34' },
  { label: "L", details: 'Large — Chest 42"–44", Waist 34–36' },
  { label: "XL", details: 'Extra Large — Chest 46"–48", Waist 36–38' },
  { label: "XXL", details: '2X Large — Chest 50"–52", Waist 38–40' },
  { label: "XXXL", details: '3X Large — Chest 54"–56", Waist 40–42' },
];

const ProductDetail = () => {
  const { id } = useParams();
  const { cart, setCart } = useCart();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing
  const [imageIndex, setImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeColor, setActiveColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [hasAdded, setHasAdded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    setImageIndex(0);
    setHasAdded(false);

    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/products/${id}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data._id) {
            setProduct(data);
            setActiveColor(data.colors?.length ? data.colors[0] : null);
            setStatus("ready");
            return;
          }
        }
        throw new Error("not found");
      } catch {
        // Demo items (used while the API is offline) live only in the bundle
        const demo = demoProducts.find((p) => p.id === id || p._id === id);
        if (demo) {
          setProduct(demo);
          setStatus("ready");
        } else {
          setStatus("missing");
        }
      }
    };
    load();
  }, [id]);

  if (status === "loading") {
    return (
      <div className={styles.page}>
        <p className={styles.state}>Loading piece…</p>
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className={styles.page}>
        <p className={styles.state}>
          We couldn't find that piece. <Link to="/shop">Browse the shop →</Link>
        </p>
      </div>
    );
  }

  // The active colour's photos take over the gallery; fall back to the
  // product's own photos when it has no colours (or the colour has none).
  const colorImages = activeColor
    ? [activeColor.image, ...(activeColor.images || [])].filter(Boolean)
    : [];
  const images = colorImages.length
    ? colorImages
    : product.image
    ? [product.image, ...(product.images || [])]
    : product.images || [];

  const chooseColor = (color) => {
    setActiveColor(color);
    setImageIndex(0);
  };

  const addToCart = () => {
    const key = product._id || product.id;
    const colorName = activeColor?.name || "";
    // Same product in a different colour is its own cart line
    const matches = (item) =>
      (item.product._id || item.product.id) === key &&
      (item.color || "") === colorName;
    const exists = cart.some(matches);
    if (exists) {
      setCart((current) =>
        current.map((item) =>
          matches(item) ? { ...item, quantity: item.quantity + quantity } : item
        )
      );
    } else {
      setCart((current) => [
        ...current,
        { product, quantity, color: colorName },
      ]);
    }
    setHasAdded(true);
  };

  const formattedPrice = new Intl.NumberFormat("en-NG").format(product.price);
  const categoryName =
    product.category?.name ||
    (typeof product.category === "string" ? product.category : "");

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Link to="/shop" className={styles.back}>
          ← Back to shop
        </Link>

        <div className={styles.layout}>
          <div className={styles.gallery}>
            <div className={styles.mainFrame}>
              {product.outOfStock && <span className={styles.soldOut}>Sold out</span>}
              <ImageWithPlaceholder
                src={images[imageIndex]}
                alt={product.name}
                className={styles.mainImg}
                wrapperStyle={{ position: "absolute", inset: 0, display: "block" }}
              />
            </div>
            {images.length > 1 && (
              <div className={styles.thumbs}>
                {images.map((url, i) => (
                  <button
                    type="button"
                    key={`${url}-${i}`}
                    className={i === imageIndex ? styles.thumbActive : styles.thumb}
                    onClick={() => setImageIndex(i)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={url} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.info}>
            <span className={styles.eyebrow}>
              {product.productCollection?.name || product.brand || "Lormami"}
            </span>
            <h1 className={styles.name}>{product.name}</h1>
            <p className={styles.price}>₦ {formattedPrice}</p>

            {(product.colors || []).length > 0 && (
              <>
                <p className={styles.label} style={{ marginTop: "0.2rem" }}>
                  Colour{activeColor ? ` — ${activeColor.name}` : ""}
                </p>
                <div className={styles.swatches}>
                  {product.colors.map((color) => (
                    <button
                      type="button"
                      key={color.name}
                      className={
                        activeColor?.name === color.name
                          ? styles.swatchActive
                          : styles.swatch
                      }
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                      aria-label={`Colour ${color.name}`}
                      onClick={() => chooseColor(color)}
                    />
                  ))}
                </div>
              </>
            )}

            {(product.rating || 0) > 0 && (
              <div className={styles.rating}>
                <span className={styles.stars}>
                  {[...Array(5)].map((_, index) => (
                    <FaStar
                      key={index}
                      size={14}
                      color={index < product.rating ? "#C9A24B" : "#E4E1DA"}
                    />
                  ))}
                </span>
                {(product.numReviews || 0) > 0 && (
                  <span className={styles.reviews}>
                    {product.numReviews} {product.numReviews === 1 ? "review" : "reviews"}
                  </span>
                )}
              </div>
            )}

            {categoryName && <span className={styles.metaChip}>{categoryName}</span>}

            {!product.outOfStock &&
              product.stockInCount > 0 &&
              product.stockInCount <= 3 && (
                <p className={styles.lowStock}>
                  Only {product.stockInCount} left in stock
                </p>
              )}

            {product.description && <p className={styles.desc}>{product.description}</p>}
            {product.richDescription && (
              <p className={styles.rich}>{product.richDescription}</p>
            )}

            {getDesigners(product).length > 0 && (
              <p className={styles.credit}>
                Designed by <DesignerNames product={product} />
              </p>
            )}

            <p className={styles.label}>Select Size</p>
            <div className={styles.sizes}>
              {SIZES.map((size) => (
                <button
                  type="button"
                  key={size.label}
                  className={
                    selectedSize?.label === size.label
                      ? styles.sizePillActive
                      : styles.sizePill
                  }
                  onClick={() => setSelectedSize(size)}
                >
                  {size.label}
                </button>
              ))}
            </div>
            {selectedSize && <p className={styles.sizeDetail}>{selectedSize.details}</p>}

            <p className={styles.label}>Quantity</p>
            <div className={styles.qtyRow}>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.stepBtn}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className={styles.qtyValue}>{quantity}</span>
                <button
                  type="button"
                  className={styles.stepBtn}
                  onClick={() => setQuantity((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>

            {hasAdded && (
              <p className={styles.added}>Added to your cart.</p>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.addBtn}
                onClick={addToCart}
                disabled={!!product.outOfStock}
              >
                {product.outOfStock ? "Sold out" : "Add to Cart"}
              </button>
              <Link to="/cart" className={styles.cartBtn}>
                Go to Cart
              </Link>
            </div>
            {!product.outOfStock && (
              <a
                className={styles.whatsappBtn}
                target="_blank"
                rel="noopener noreferrer"
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                  `Hello LORMAMI! I'd like to order: ${product.name} (₦${formattedPrice})` +
                    (activeColor ? `, ${activeColor.name}` : "") +
                    (selectedSize ? `, size ${selectedSize.label}` : "") +
                    ` × ${quantity}. ${window.location.href}`
                )}`}
              >
                Order on WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetail;
